import React, {
  createContext,
  useState,
  useEffect,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useLocation } from "react-router-dom";
import { useMyPaginatedBlogs } from "../hooks/useMyPaginatedBlogs";
import type { Blog } from "../types/models";

/**
 * BlogContext - Manages blog-specific state and operations
 * 
 * NOTE: This context is specifically for blogs only. Stories use GraphQL hooks directly
 * (useMyStories, usePaginatedStories, etc.) and do not require a context.
 * This separation ensures backward compatibility and keeps the systems independent.
 * 
 * For stories, use the hooks directly:
 * - useMyStories() - for user's stories with pagination
 * - usePaginatedStories() - for paginated story queries
 * - useStories() - for general story queries
 */
interface BlogContextValue {
  blogs: Blog[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  addBlog: (newBlog: Partial<Blog>) => void;
  editBlog: (updatedBlog: Partial<Blog>) => void;
  deleteBlog: (deletedBlogId: string) => void;
}

export const BlogContext = createContext<BlogContextValue | undefined>(
  undefined
);

interface BlogProviderProps {
  children: ReactNode;
}

export const BlogProvider = ({ children }: BlogProviderProps): JSX.Element => {
  const location = useLocation();
  const pageSize = 6;
  const [page, setPage] = useState(1);

  // Only fetch blogs when on blog-related pages to avoid unnecessary API calls
  const isBlogPage = location.pathname.includes("/blog") || 
                     location.pathname.includes("/my-blogs");

  const { blogs, currentPage, totalPages, isLoading, refetchPage } =
    useMyPaginatedBlogs(page, pageSize, !isBlogPage); // Skip query if not on blog page

  useEffect(() => {
    // Only refetch when on blog pages and when page changes
    if (isBlogPage) {
      refetchPage({ page, pageSize });
    }
  }, [page, pageSize, refetchPage, isBlogPage]);

  const refetch = () => {
    if (isBlogPage) {
      refetchPage({ page, pageSize });
    }
  };

  const addBlog = (_newBlog: Partial<Blog>) => {
    refetch();
  };

  const deleteBlog = (_deletedBlogId: string) => {
    refetch();
  };

  const editBlog = (_updatedBlog: Partial<Blog>) => {
    refetch();
  };

  return (
    <BlogContext.Provider
      value={{
        blogs,
        isLoading,
        currentPage,
        totalPages,
        page,
        setPage,
        addBlog,
        editBlog,
        deleteBlog,
      }}
    >
      {children}
    </BlogContext.Provider>
  );
};

export const useBlogContext = (): BlogContextValue => {
  const context = React.useContext(BlogContext);
  if (!context) {
    throw new Error("useBlogContext must be used within a BlogProvider");
  }
  return context;
};
